import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isSuperAdmin } from '@/services/admin.service';

export interface OnboardingStatus {
  isOnboardingComplete: boolean;
  onboardingStep: number;
  needsOrganizationName: boolean;
  needsPhone: boolean;
  needsEmail: boolean;
  isLoading: boolean;
}

export const useOnboardingStatus = (): OnboardingStatus => {
  const { user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus>({
    isOnboardingComplete: false,
    onboardingStep: 0,
    needsOrganizationName: false,
    needsPhone: false,
    needsEmail: false,
    isLoading: true,
  });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setStatus(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        // Check if user is admin first - admins skip onboarding
        let isAdmin = false;
        try {
          isAdmin = await isSuperAdmin(user.id);
        } catch (error) {
          console.log('Admin check failed during onboarding check:', error);
        }

        if (isAdmin) {
          setStatus({
            isOnboardingComplete: true,
            onboardingStep: 0,
            needsOrganizationName: false,
            needsPhone: false,
            needsEmail: false,
            isLoading: false,
          });
          return;
        }

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('organization_name, phone, email, onboarding_completed, onboarding_step, onboarding_skipped')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          setStatus(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const needsOrganizationName = !profile?.organization_name;
        const needsPhone = !profile?.phone;
        const needsEmail = !profile?.email;
        const isOnboardingComplete = profile?.onboarding_completed || profile?.onboarding_skipped;

        setStatus({
          isOnboardingComplete,
          onboardingStep: profile?.onboarding_step || 0,
          needsOrganizationName,
          needsPhone,
          needsEmail,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setStatus(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkOnboardingStatus();
  }, [user, isAuthenticated]);

  return status;
};